import { Moon } from 'lucide-react'
import { useMemo } from 'react'

import type { GarminSleepRaw } from '@/api/types/garmin'
import { Card, CardContent } from '@/components/ui/card'
import { formatDurationSeconds, formatTimestampGMT } from '@/lib/activity-format-utils'

import type { SleepLevel } from '../sleep-graph'
import { SleepGraph } from '../sleep-graph'

type SleepCardProps = {
  sleep: GarminSleepRaw
  onClick?: () => void
}

export function SleepCard({ sleep, onClick }: SleepCardProps) {
  const daily = sleep.dailySleepDTO

  // Raw → SleepGraph用に変換（Hookはトップレベルで呼ぶ）
  // startGMT/endGMT は "2026-02-02T15:23:22.0" 形式のGMT文字列。
  // タイムゾーン指定なしだとローカルタイムとして解釈されるため、末尾にZを付与してUTCとしてパースする。
  const levels: SleepLevel[] = useMemo(
    () =>
      (sleep.sleepLevels ?? []).map((l) => ({
        startTime: new Date(l.startGMT.replace(/\.0$/, '') + 'Z').toISOString(),
        endTime: new Date(l.endGMT.replace(/\.0$/, '') + 'Z').toISOString(),
        level: l.activityLevel,
      })),
    [sleep.sleepLevels],
  )

  if (!daily) return null

  const deepSeconds = daily.deepSleepSeconds ?? 0
  const lightSeconds = daily.lightSleepSeconds ?? 0
  const remSeconds = daily.remSleepSeconds ?? 0
  const awakeSeconds = daily.awakeSleepSeconds ?? 0
  const score = daily.sleepScores?.overall?.value
  const totalStages = deepSeconds + lightSeconds + remSeconds + awakeSeconds

  const startTime = formatTimestampGMT(daily.sleepStartTimestampGMT)
  const endTime = formatTimestampGMT(daily.sleepEndTimestampGMT)

  const startIso = daily.sleepStartTimestampGMT ? new Date(daily.sleepStartTimestampGMT).toISOString() : null
  const endIso = daily.sleepEndTimestampGMT ? new Date(daily.sleepEndTimestampGMT).toISOString() : null

  return (
    <Card className={`hover:shadow-md transition-shadow p-0${onClick ? ' cursor-pointer' : ''}`} onClick={onClick}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-indigo-100">
            <Moon className="h-5 w-5 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-medium">{sleep.kind === 'nap' ? '昼寝' : '睡眠'}</h3>
              <span className="text-xs text-muted-foreground shrink-0">
                {startTime} - {endTime}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">
                {formatDurationSeconds(totalStages > 0 ? totalStages : daily.sleepTimeSeconds)}
              </span>
              {score != null && (
                <>
                  <span>•</span>
                  <span>スコア {score}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 睡眠グラフ（タイムライン + 凡例） */}
        {totalStages > 0 && (
          <div className="mt-3">
            <SleepGraph
              startTime={startIso}
              endTime={endIso}
              levels={levels}
              stages={{ deep: deepSeconds, light: lightSeconds, rem: remSeconds, awake: awakeSeconds }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
